import { skipToken } from '@tanstack/react-query';
import { Linkedin } from 'lucide-react';
import pluralize from 'pluralize';
import { useState } from 'react';
import { toast } from 'sonner';

import { useWorkspacePlan } from '@/modules/billing/hooks/useWorkspacePlan';
import { lacksFeature } from '@/modules/billing/workspacePlan';
import {
  IntegrationRow,
  IntegrationStatus,
} from '@/modules/hiring-manager/components/IntegrationRow';
import { countJobsPausedByDisconnect } from '@/modules/hiring-manager/utils/outreachReadiness';
import { useLeavingPage } from '@/modules/integrations/hooks/useLeavingPage';
import { useJobsQuery } from '@/modules/jobs/queries/useJobsQuery';
import { useLegalDocumentsQuery } from '@/modules/legal/queries/useLegalDocumentsQuery';
import { Button } from '@/modules/ui/components/button/Button';
import { ConfirmDialog } from '@/modules/ui/components/confirm-dialog/ConfirmDialog';
import { useIsLinkedInConnected } from '@/modules/user-external-accounts/hooks/useIsLinkedInConnected';
import { actionErrorMessage } from '@/utils/actionErrorMessage';

import { isLinkedInLockedByPlan } from './linkedInPlanGate';
import { LockedLinkedInRow } from './LockedLinkedInRow';
import { useConnectHiringManagerLinkedInMutation } from './useConnectHiringManagerLinkedInMutation';
import { useDisconnectHiringManagerLinkedInMutation } from './useDisconnectHiringManagerLinkedInMutation';
import { useHiringManagerLinkedInAccountQuery } from './useHiringManagerLinkedInAccountQuery';

interface LinkedInIntegrationCardProps {
  tenantId: string;
  /** Billing page link, including the tenant search when applicable. */
  billingHref: string;
}

/**
 * A tenant-authorized LinkedIn row that mirrors the portal's Slack setting.
 * A plan without outreach gets `LockedLinkedInRow` instead of the connect action.
 */
export function LinkedInIntegrationCard({
  tenantId,
  billingHref,
}: LinkedInIntegrationCardProps) {
  const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = useState(false);
  const isOutreachLocked = lacksFeature(useWorkspacePlan(), 'outreach');
  const accountQuery = useHiringManagerLinkedInAccountQuery(tenantId);
  const connectMutation = useConnectHiringManagerLinkedInMutation();
  const disconnectMutation = useDisconnectHiringManagerLinkedInMutation();
  // Clicking Connect records that the user saw this warning, so it shows beside the button.
  const automationWarning = useLegalDocumentsQuery().data?.find(
    (document) => document.type === 'LinkedInAutomationWarning',
  )?.text;
  const account = accountQuery.data;
  const isActive = account?.status === 'Active';
  const hasStatusError = accountQuery.isError && account === undefined;
  const actionLabel = account ? 'Reconnect LinkedIn' : 'Connect LinkedIn';
  // Same query as the Jobs page, so it is usually cached; only a connected account can pause anything.
  const { userId, linkedInStatus } = useIsLinkedInConnected();
  const jobsQuery = useJobsQuery(
    isActive ? { companyId: tenantId } : skipToken,
  );
  // Unknown while the jobs load or fail; the warning then names no count.
  const pausedJobs =
    jobsQuery.data && userId
      ? countJobsPausedByDisconnect(jobsQuery.data, userId, linkedInStatus)
      : undefined;
  const disconnectDescription =
    pausedJobs === 0
      ? 'Disconnecting removes this LinkedIn account from Finlay. You can connect it again later.'
      : pausedJobs === undefined
        ? 'Disconnecting removes this LinkedIn account from Finlay. Outreach on your jobs pauses until you reconnect it.'
        : `Disconnecting removes this LinkedIn account from Finlay. Outreach on ${pluralize('job', pausedJobs, true)} pauses until you reconnect it.`;

  const linkedInRedirect = useLeavingPage();

  const connect = () => {
    connectMutation.mutate(tenantId, {
      onSuccess: (result) => {
        if (result?.signupLink) {
          linkedInRedirect.startLeaving();
          window.location.assign(result.signupLink);
          return;
        }

        // LinkedIn answered without a sign-in link to send the user to.
        toast.error("Couldn't connect LinkedIn. Try again in a moment.");
      },
      onError: (error) => {
        toast.error(actionErrorMessage('connect LinkedIn', error));
      },
    });
  };

  // The row flipping to "Not connected" confirms success; a failure stays in the dialog.
  const disconnect = () => {
    disconnectMutation.mutate(tenantId, {
      onSuccess: () => changeDisconnectDialog(false),
    });
  };

  const changeDisconnectDialog = (open: boolean) => {
    // Reopening starts clean instead of showing the last attempt's error.
    if (!open) disconnectMutation.reset();
    setIsDisconnectDialogOpen(open);
  };

  if (isLinkedInLockedByPlan(isOutreachLocked, account)) {
    return <LockedLinkedInRow to={billingHref} />;
  }

  const status = accountQuery.isLoading
    ? 'Checking connection'
    : hasStatusError
      ? 'Status unavailable'
      : isActive
        ? 'Connected'
        : account
          ? account.status
          : 'Not connected';

  return (
    <>
      <IntegrationRow
        action={
          isActive ? (
            <Button
              aria-label="Disconnect LinkedIn"
              className="h-8 rounded-md"
              onClick={() => setIsDisconnectDialogOpen(true)}
              size="sm"
              variant="outline"
            >
              Disconnect
            </Button>
          ) : (
            <Button
              className="h-8 rounded-md"
              disabled={
                accountQuery.isLoading || hasStatusError || !automationWarning
              }
              loading={connectMutation.isPending || linkedInRedirect.isLeaving}
              onClick={connect}
              size="sm"
            >
              {actionLabel}
            </Button>
          )
        }
        description="Finlay sends connection requests and messages from your LinkedIn profile."
        icon={Linkedin}
        name="LinkedIn"
        status={
          <IntegrationStatus isActive={isActive}>{status}</IntegrationStatus>
        }
        usedBy="Outreach"
      >
        {!isActive && automationWarning && (
          <p className="text-muted-foreground text-[13px] leading-[19px]">
            {automationWarning}
          </p>
        )}
      </IntegrationRow>

      <ConfirmDialog
        confirmText="Disconnect"
        confirming={disconnectMutation.isPending}
        confirmingText="Disconnecting…"
        description={disconnectDescription}
        error={
          disconnectMutation.isError
            ? actionErrorMessage(
                'disconnect LinkedIn',
                disconnectMutation.error,
              )
            : null
        }
        onConfirm={disconnect}
        onOpenChange={(open) =>
          !disconnectMutation.isPending && changeDisconnectDialog(open)
        }
        open={isDisconnectDialogOpen}
        title="Disconnect LinkedIn account?"
        variant="destructive"
      />
    </>
  );
}
